import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Button,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import InfoIcon from '@material-ui/icons/Info';
import { QuickAccessLink } from './HomePage';

const useStyles = makeStyles((theme) => ({
  root: {
    '&:first-child': {
      borderTop: 'none',
    },
    '&:before': {
      display: 'none',
    },
  },
  summary: {
    backgroundColor: 'transparent',
    borderBottom: `1px solid ${theme.palette.divider}`,
    minHeight: 48,
    '&$expanded': {
      minHeight: 48,
    },
    '& .MuiTypography-root': {
      color: theme.palette.text.primary,
      fontWeight: '600 !important',
      fontSize: '1rem !important',
    },
    '& .MuiTypography-subtitle1': {
      color: theme.palette.text.primary,
      fontWeight: '600 !important',
      fontSize: '1rem !important',
    },
    '& h3': {
      color: theme.palette.text.primary,
      fontWeight: '600 !important',
      fontSize: '1rem !important',
    },
    '& .MuiSvgIcon-root': {
      color: theme.palette.text.primary,
    },
    '& .MuiIconButton-root': {
      color: theme.palette.text.primary,
    },
  },
  summaryContent: {
    margin: `${theme.spacing(1)}px 0`,
    '&$expanded': {
      margin: `${theme.spacing(1)}px 0`,
    },
  },
  expanded: {},
  details: {
    padding: 0,
    display: 'block',
  },
  list: {
    width: '100%',
    padding: 0,
  },
  listItem: {
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  listItemIcon: {
    minWidth: 48,
  },
  listItemContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  detailsButton: {
    padding: theme.spacing(0.5, 1),
    minWidth: 'auto',
    marginLeft: theme.spacing(1),
  },
}));

interface EnhancedHomePageToolkitProps {
  title: string;
  links: QuickAccessLink[];
  expanded?: boolean;
  onLinkClick: (link: QuickAccessLink) => void;
  onDetailsClick: (link: QuickAccessLink) => void;
}

export const EnhancedHomePageToolkit: React.FC<EnhancedHomePageToolkitProps> = ({
  title,
  links,
  expanded = false,
  onLinkClick,
  onDetailsClick,
}) => {
  const classes = useStyles();

  return (
    <Accordion
      defaultExpanded={expanded}
      className={classes.root}
      classes={{
        expanded: classes.expanded,
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        className={classes.summary}
        classes={{
          content: classes.summaryContent,
          expanded: classes.expanded,
        }}
      >
        <Typography
          variant="subtitle1"
          component="h3"
          style={{
            fontWeight: 600,
            fontSize: '1rem'
          }}
        >
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails className={classes.details}>
        <List className={classes.list}>
          {links.map((link, index) => (
            <ListItem
              key={`${link.label}-${index}`}
              className={classes.listItem}
              button={false}
              component="div"
            >
              <ListItemIcon className={classes.listItemIcon}>
                <img
                  src={link.iconUrl}
                  alt={link.label}
                  style={{ height: 32, width: 32, borderRadius: 4 }}
                />
              </ListItemIcon>
              <Box className={classes.listItemContent}>
                <ListItemText
                  primary={link.label}
                  style={{ cursor: 'pointer' }}
                  onClick={() => onLinkClick(link)}
                />
                {link.details && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<InfoIcon />}
                    className={classes.detailsButton}
                    onClick={() => onDetailsClick(link)}
                  >
                    Details
                  </Button>
                )}
              </Box>
            </ListItem>
          ))}
        </List>
      </AccordionDetails>
    </Accordion>
  );
};